/**
 * MKS-372 — which Default-payment-type projects are ready for Time & Material.
 *
 * One row per project still on the Default payment type (the MKS-211 scope
 * predicate, reused server-side), with `ready` or `n issues` and a link into
 * the project. The readiness is the SAME service the project form's
 * pre-conversion gate runs, batched into one request, so this page can never
 * say "ready" about a project whose save is then refused.
 *
 * Shaped after `EntriesNeedingRolePage` — the closest existing small admin
 * page — and routed under `AdminRoute` in `App.tsx`.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '../../components/common/Spinner';
import { useFlag } from '../../services/flagsContext';
import { useDefaultProjectReadiness } from '../../hooks/useTmConversion';
import { toErrorMessage } from '../../utils/toErrorMessage';

export default function TmConversionReadinessPage() {
  const tmConversionOn = useFlag('mks_372_tm_conversion_check');
  const query = useDefaultProjectReadiness(tmConversionOn);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggle = (projectId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  if (!tmConversionOn) {
    return (
      <div className="max-w-4xl mx-auto">
        <p className="text-sm text-gray-600">This page is not available yet.</p>
      </div>
    );
  }

  const rows = query.data ?? [];
  const readyCount = rows.filter((r) => r.ready).length;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          Ready for Time &amp; Material
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Every project still on the Default payment type. On Time &amp; Material the
          rate of a role is the rate of everyone in it, so a project converts cleanly
          only once each person has a role, each role has one rate, and the currencies
          agree.
        </p>
      </div>

      {query.isLoading && <Spinner />}

      {query.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">
            {toErrorMessage(query.error, 'Could not load the readiness list.')}
          </p>
        </div>
      )}

      {!query.isLoading && !query.isError && rows.length === 0 && (
        <p className="text-sm text-gray-600">
          No project is on the Default payment type.
        </p>
      )}

      {rows.length > 0 && (
        <>
          <p className="text-sm text-gray-600">
            {readyCount} of {rows.length} ready.
          </p>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Project
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Readiness
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((row) => (
                  <tr key={row.projectId} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 text-sm">
                      <Link
                        to={`/projects/${row.projectId}`}
                        className="text-blue-700 underline"
                      >
                        {row.projectName ?? `#${row.projectId}`}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {row.ready ? (
                        <span className="text-green-800">ready</span>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="text-amber-800 underline"
                            onClick={() => toggle(row.projectId)}
                          >
                            {row.issueCount} {row.issueCount === 1 ? 'issue' : 'issues'}
                          </button>
                          {expanded.has(row.projectId) && (
                            <ul className="mt-2 list-disc pl-5 space-y-1 text-xs text-gray-700">
                              {row.issues.map((issue, idx) => (
                                <li key={`${issue.type}-${issue.roleId ?? 'none'}-${idx}`}>
                                  {issue.message}
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <Link
                        to={`/projects/${row.projectId}/edit`}
                        className="text-blue-700 underline"
                      >
                        Edit project
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
